/**
 * Application constants and configuration
 */

/**
 * App Info
 */
export const APP_NAME = 'ResearchConstellation'

/**
 * API Configuration
 */
export const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/v1'
export const API_TIMEOUT = 30000 // 30 seconds

/**
 * Authentication
 */
export const TOKEN_STORAGE_KEY = 'rc_access_token'
export const REFRESH_TOKEN_KEY = 'rc_refresh_token'

/**
 * Feature Flags
 */
export const ENABLE_DEVTOOLS = import.meta.env.DEV

/**
 * Platform Modules Configuration
 */
export interface Module {
  id: string
  name: string
  title: string
  description: string
  path: string
  icon: string
  color: string
}

export const MODULES: Module[] = [
  {
    id: 'sagitta',
    name: 'Sagitta',
    title: 'Bandi & Opportunità',
    description: 'Scopri bandi, crea alert e genera proposte AI',
    path: '/sagitta',
    icon: '🏹',
    color: 'text-green-500',
  },
  {
    id: 'cassiopeia',
    name: 'Cassiopeia',
    title: 'Mappa Ricerca',
    description: 'Tecnologie, ricercatori e innovazioni universitarie',
    path: '/cassiopeia',
    icon: '🔬',
    color: 'text-blue-500',
  },
  {
    id: 'andromeda',
    name: 'Andromeda',
    title: 'Consorzio',
    description: 'Trova partner e costruisci il consorzio',
    path: '/andromeda',
    icon: '🤝',
    color: 'text-purple-500',
  },
  {
    id: 'cepheus',
    name: 'Cepheus',
    title: 'Gestione Progetti',
    description: 'Progetti, deliverable e workflow',
    path: '/cepheus',
    icon: '📊',
    color: 'text-orange-500',
  },
  {
    id: 'pegasus',
    name: 'Pegasus',
    title: 'Disseminazione',
    description: 'Letteratura e piano di disseminazione',
    path: '/pegasus',
    icon: '📚',
    color: 'text-cyan-500',
  },
  {
    id: 'workspaces',
    name: 'Workspace',
    title: 'Consulenti',
    description: 'Gestisci clienti e portfolio consulenziale',
    path: '/workspaces',
    icon: '💼',
    color: 'text-indigo-500',
  },
  {
    id: 'observatory',
    name: 'Osservatorio',
    title: 'Analisi & Trend',
    description: 'Indicatori aggregati e analisi territoriale',
    path: '/observatory',
    icon: '🔭',
    color: 'text-teal-500',
  },
]

/**
 * React Query Keys
 */
export const QUERY_KEYS = {
  // Auth
  USER: ['user'] as const,

  // Cassiopeia
  RESEARCHERS: ['researchers'] as const,
  RESEARCHER: (id: string) => ['researchers', id] as const,
  PUBLICATIONS: ['publications'] as const,
  RESEARCH_GROUPS: ['research-groups'] as const,
  RESEARCH_GROUP: (id: string) => ['research-groups', id] as const,
  UNIVERSITIES: ['universities'] as const,
  CASSIOPEIA_SEARCH: ['cassiopeia-search'] as const,
  CASSIOPEIA_EXPLAIN: ['cassiopeia-explain'] as const,

  // Sagitta
  GRANTS: ['grants'] as const,
  GRANT: (id: string) => ['grants', id] as const,
  GRANT_ALERTS: ['grant-alerts'] as const,
  GRANT_MATCHES: ['grant-matches'] as const,
  CURATION_TICKETS: ['curation-tickets'] as const,
  ML_FEEDBACK: ['ml-feedback'] as const,

  // Lyra
  TEAMS: ['teams'] as const,
  TEAM: (id: string) => ['teams', id] as const,
  MESSAGES: ['messages'] as const,

  // Draco
  WORKFLOWS: ['workflows'] as const,
  WORKFLOW: (id: string) => ['workflows', id] as const,
  WORKFLOW_EXECUTIONS: ['workflow-executions'] as const,

  // Orion
  SEARCH: ['search'] as const,

  // Cepheus
  PROJECTS: ['projects'] as const,
  PROJECT: (id: string) => ['projects', id] as const,
  MILESTONES: ['milestones'] as const,

  // Technology Graph
  TECHNOLOGIES: ['technologies'] as const,
  TECHNOLOGY: (id: string) => ['technologies', id] as const,
  TECHNOLOGY_SEARCH: ['technology-search'] as const,
  TECHNOLOGY_GRAPH: ['technology-graph'] as const,
  COMPANY_MATCH: ['company-match'] as const,

  // Scientific Alerts
  SCIENTIFIC_ALERTS: ['scientific-alerts'] as const,
  SCIENTIFIC_ALERT_FEED: ['scientific-alert-feed'] as const,
  SCIENTIFIC_ALERT_DIGEST: ['scientific-alert-digest'] as const,

  // Integration
  GRANT_GROUP_MATCHES: ['grant-group-matches'] as const,
  GROUP_GRANT_MATCHES: ['group-grant-matches'] as const,
  FUNDING_WRAPPER: ['funding-wrapper'] as const,
  CONSORTIUM_SUGGESTIONS: ['consortium-suggestions'] as const,
  CONSORTIUM_PARTNERS: ['consortium-partners'] as const,
  EVENT_SUGGESTIONS: ['event-suggestions'] as const,
  EVENT_ANALYTICS: ['event-analytics'] as const,
} as const

/**
 * API Routes
 */
export const API_ROUTES = {
  // Auth
  AUTH: {
    LOGIN: '/auth/token',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
    TOKEN: '/auth/token',
  },

  // Cassiopeia
  CASSIOPEIA: {
    RESEARCHERS: '/cassiopeia/researchers',
    RESEARCHER: (id: string) => `/cassiopeia/researchers/${id}`,
    PUBLICATIONS: '/cassiopeia/publications',
    PROJECTS: '/cassiopeia/projects',
    UNIVERSITIES: '/cassiopeia/universities',
    GROUPS: '/cassiopeia/groups',
    GROUP: (id: string) => `/cassiopeia/groups/${id}`,
    SEARCH: '/cassiopeia/search',
    SEARCH_PUBLICATIONS: '/cassiopeia/search/publications',
    SEARCH_RESEARCHERS: '/cassiopeia/search/researchers',
    SEARCH_PROJECTS: '/cassiopeia/search/projects',
    EXPLAIN: '/cassiopeia/explain',
    EXPLAIN_RESEARCHER: (id: string) => `/cassiopeia/explain/researcher/${id}`,
    EXPLAIN_GROUP: (id: string) => `/cassiopeia/explain/group/${id}`,
    EXPLAIN_TOPIC: '/cassiopeia/explain/topic',
    RANK_PUBLICATIONS: '/cassiopeia/rank/publications',
    RANK_RESEARCHERS: '/cassiopeia/rank/researchers',
    RANK_GROUPS: '/cassiopeia/rank/groups',
    HUB_PROFILE: '/cassiopeia/hub/profile',
    HUB_DASHBOARD: '/cassiopeia/hub/dashboard',
    HUB_NOTIFICATIONS: '/cassiopeia/hub/notifications',
    AUTH_ORCID: '/cassiopeia/auth/orcid/login',
    OVERVIEW: '/cassiopeia/overview',
    GRAPH_TOPIC_RESEARCHERS: (topic: string) =>
      `/cassiopeia/graph/topic/${encodeURIComponent(topic)}/researchers`,
    GRAPH_RESEARCHER_NETWORK: (id: string) => `/cassiopeia/graph/researcher/${id}/network`,
    MONITORING_HEALTH: '/cassiopeia/monitoring/health',
    MONITORING_QUALITY: '/cassiopeia/monitoring/quality-score',
    TECHNOLOGIES_SEARCH: '/cassiopeia/technologies/search',
    TECHNOLOGY_DETAIL: (id: string) => `/cassiopeia/technologies/${id}`,
    TECHNOLOGIES_CATALOG: '/cassiopeia/technologies/catalog',
    TECHNOLOGIES_MATCH: '/cassiopeia/technologies/match',
    GRAPH_EXPLORE: (nodeType: string, nodeId: string) =>
      `/cassiopeia/graph/explore/${nodeType}/${nodeId}`,
    GRAPH_STATS: '/cassiopeia/graph/stats',
    GRAPH_THEMATIC_MAP: '/cassiopeia/graph/thematic-map',
    GRAPH_THEMATIC_PUBLICATIONS: (researcherId: string) =>
      `/cassiopeia/graph/thematic-map/${researcherId}/publications`,
    LITERATURE_SEARCH: '/cassiopeia/literature/search',
    LITERATURE_VERIFY: '/cassiopeia/literature/verify',
  },

  // Sagitta
  SAGITTA: {
    GRANTS: '/sagitta/grants',
    GRANT: (id: string) => `/sagitta/grants/${id}`,
    ALERTS: '/sagitta/alerts',
    MATCHES: '/sagitta/matches',
    COMPANY_PROFILES: '/sagitta/company-profiles',
    CURATION_TICKETS: '/sagitta/curation/tickets',
    CURATION_TICKET: (id: string) => `/sagitta/curation/tickets/${id}`,
    ML_FEEDBACK: '/sagitta/ml/feedback',
    ACTIVE_LEARNING_SAMPLES: '/sagitta/ml/active-learning/samples',
    EXTRACTION_QUALITY: '/sagitta/extraction/quality',
  },

  // Lyra
  LYRA: {
    TEAMS: '/lyra/teams',
    TEAM: (id: string) => `/lyra/teams/${id}`,
    MESSAGES: '/lyra/messages',
  },

  // Draco
  DRACO: {
    WORKFLOWS: '/draco/workflows',
    WORKFLOW: (id: string) => `/draco/workflows/${id}`,
    EXECUTIONS: '/draco/executions',
    TEMPLATES: '/draco/templates',
  },

  // Orion
  ORION: {
    SEARCH: '/orion/search',
    SUGGEST: '/orion/suggest',
  },

  // Cepheus
  CEPHEUS: {
    PROJECTS: '/cepheus/projects',
    PROJECT: (id: string) => `/cepheus/projects/${id}`,
    MILESTONES: '/cepheus/milestones',
    DELIVERABLES: '/cepheus/deliverables',
  },

  // Pegasus
  PEGASUS: {
    REPORTS_GENERATE: '/pegasus/reports/generate',
    REPORTS_CONVERT: '/pegasus/reports/convert',
  },

  // Scientific Alerts
  ALERTS: {
    SCIENTIFIC: '/alerts/scientific',
    SCIENTIFIC_DETAIL: (id: string) => `/alerts/scientific/${id}`,
    SCIENTIFIC_FEED: (alertId: string) => `/alerts/scientific/${alertId}/feed`,
    SCIENTIFIC_FEED_READ: (alertId: string, itemId: string) =>
      `/alerts/scientific/${alertId}/feed/${itemId}/read`,
    DIGEST: '/alerts/digest',
  },

  // Integration - Cross-Module API
  INTEGRATION: {
    // Grant-Research Matching
    MATCH_GROUPS_FOR_GRANT: '/integration/grants/match-groups',
    MATCH_GRANTS_FOR_GROUP: '/integration/groups/match-grants',
    FUNDING_WRAPPER: '/integration/funding-wrapper',
    GRANT_RESEARCHERS: (grantId: string) => `/integration/grants/${grantId}/researchers`,

    // Consortium Building
    CONSORTIUM_SUGGEST: '/integration/consortium/suggest',
    CONSORTIUM_FIND_PARTNERS: '/integration/consortium/find-partners',
    CONSORTIUM_ANALYZE: '/integration/consortium/analyze',
    CONSORTIUM_HISTORY: '/integration/consortium/collaboration-history',

    // Event Integration
    EVENTS_SUGGEST: '/integration/events/suggest',
    EVENTS_PLAN_RESEARCH_DAY: '/integration/events/plan-research-day',
    EVENTS_FIND_PARTICIPANTS: '/integration/events/find-participants',
    EVENTS_FUNDING_CONTEXT: '/integration/events/funding-context',
    EVENTS_PROPOSE: '/integration/events/propose',
    EVENTS_ANALYTICS: '/integration/events/analytics',
  },
} as const

/**
 * Pagination defaults
 */
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
} as const

/**
 * Date formats
 */
export const DATE_FORMATS = {
  DISPLAY: 'MMM d, yyyy',
  DISPLAY_WITH_TIME: 'MMM d, yyyy HH:mm',
  API: 'yyyy-MM-dd',
  API_WITH_TIME: "yyyy-MM-dd'T'HH:mm:ss",
} as const
